import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getCoachCommissionTables } from '../coachCommissionTable/api';
import { coachCommissionTableKeys } from '../coachCommissionTable/keys';
import { sessionReportKeys } from '../sessionReport/keys';
import { putNoshowAlwaysIn } from './api';
import { YearMonth } from './rules';
import { ZeroableInRateCoach } from './types';

/**
 * 「노쇼 수업은 항상 IN」 저장.
 *
 * 노쇼 수업의 IN/OUT·수업료가 바뀌므로 급여·수업료 정산·매출 캘린더·세션 현황을 무효화한다.
 * 요율 목록은 다시 읽지 않는다(refetchType: 'none') — 같은 탭에서 편집 중인 구간표가 서버 값으로 되돌아가지 않게.
 * 화면 표시는 호출부가 응답값으로 직접 고친다(예외 수업료 저장과 같은 관례).
 */
export const useSaveNoshowAlwaysIn = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: putNoshowAlwaysIn,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: coachCommissionTableKeys.all, refetchType: 'none' });
            queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] });
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] });
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] });
            queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] });
            queryClient.invalidateQueries({ queryKey: sessionReportKeys.all });
        },
    });
};

/**
 * 켜기 전 확인용 — 고른 시작 달의 요율표에서 IN 요율이 0원이 될 수 있는 강사(서버 inRateZeroable 그대로).
 * 캐시를 거치지 않는다: 그 달이 지금 보고 있는 달이면 캐시 갱신이 편집 중인 구간표를 서버 값으로 되돌리기 때문이다.
 * 못 읽으면 null — 확인 창은 목록 없이 띄우고, 저장 응답의 목록을 대신 보인다.
 */
export const loadZeroableInRateCoaches = async (
    centerId: string,
    target: YearMonth,
): Promise<ZeroableInRateCoach[] | null> => {
    try {
        const res = await getCoachCommissionTables({
            centerId,
            year: String(target.year),
            month: String(target.month),
        });
        const tables = res.data?.coachCommissionTables;
        if (!Array.isArray(tables)) return null;
        return tables
            .filter((table) => !!table.inRateZeroable)
            .map((table) => ({
                coachId: table.coachId,
                name: table.coachName,
                reason: table.inRateZeroable!,
            }));
    } catch {
        return null;
    }
};
